import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  InformationalScreen,
  LoadingModal,
  Modal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import {
  Footer,
  FormContainer,
  InputPassword,
  MainContainer,
  PhoneInput,
  TopHeaderBackArrow,
} from '~/components/utility';
import { APP_RETURN_TIMEOUT, ErrorCode } from '~/constants';
import { type RootState } from '~/reducers';
import { AUTH_SESSION } from '~/reducers/auth/types';
import { signIn } from '~/services/auth';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import { cleanPhoneNumber } from '~/utilities/cleanPhoneNumber';
import i18n from '~/utilities/i18n';
import log from '~/utilities/log';
import mediator from '~/utilities/mediator';

type MyFormValues = {
  phone: string;
  password: string;
};

const initialValues: MyFormValues = { phone: '', password: '' };

const mapStateToProps = ({
  nav: { query },
  env: { deviceId, osType, osVersion, appVersion },
  config: { allowedCountries, checkAllowedCountries },
}: RootState) => ({
  allowedCountries,
  checkAllowedCountries,
  deviceId,
  osType,
  osVersion,
  appVersion,
  query,
  phone: cleanPhoneNumber(query.phone),
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'sign-in';

const SignIn = ({
  deviceId,
  osType,
  osVersion,
  appVersion,
  query,
  phone,
  checkAllowedCountries,
  allowedCountries,
  dispatch,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = z.object({
    phone: z
      .string(
        i18n.t('SignIn.content.signInForm.formField.phone.errors.required'),
      )
      .min(
        1,
        i18n.t('SignIn.content.signInForm.formField.phone.errors.required'),
      )
      .trim()
      .refine(
        (value) => parsePhoneNumberFromString(value)?.isPossible() ?? false,
        {
          error: i18n.t(
            'SignIn.content.signInForm.formField.phone.errors.phoneNumberValid',
          ),
        },
      )
      .refine(
        (value) => {
          if (!checkAllowedCountries) {
            return true;
          }

          const phoneNumber = parsePhoneNumberFromString(value);

          if (!phoneNumber) {
            return false;
          }

          return allowedCountries.includes(phoneNumber.country ?? 'US');
        },
        {
          error: i18n.t(
            'SignIn.content.signInForm.formField.phone.errors.onlyContriesAllowed',
            {
              countries: allowedCountries.join(', '),
            },
          ),
        },
      ),
    password: z
      .string(
        i18n.t('SignIn.content.signInForm.formField.password.errors.required'),
      )
      .trim()
      .min(
        1,
        i18n.t('SignIn.content.signInForm.formField.password.errors.required'),
      ),
  });

  const signInRequest = useCallback(
    async ({ values }: { values: MyFormValues }) => {
      setIsLoading(true);

      try {
        const authSession = await signIn({
          phoneNumber: values.phone,
          password: values.password,
          appVersion,
          deviceId,
          osType,
          osVersion,
        });

        dispatch({ type: AUTH_SESSION, authSession });

        notifyApp('adc-webview:signin', authSession);

        // This timeout is added to give the app time to redirect the user back.
        setTimeout(() => {
          setIsLoading(false);
        }, APP_RETURN_TIMEOUT);
      } catch (error) {
        setIsLoading(false);

        const err = error as AxiosError<ApiErrorData>;

        const data = err.response?.data ?? {
          details:
            err.code === ErrorCode.NETWORK_ERROR
              ? ErrorCode.NETWORK_ERROR
              : i18n.t('Global.modals.errorCommunicatingServer.body'),
        };

        log.error('SignIn: Unhandled error during sign in', err);

        Modal.show({
          testID: buildTestId({
            testID,
            suffix: ['unable', 'to', 'signin', 'modal'],
          }),
          header: i18n.t(
            'SignIn.content.apiResponseErrors.unableToSignIn.title',
          ),
          details: (
            <Stack gap="$4">
              <Text>
                {i18n.t(
                  'SignIn.content.apiResponseErrors.unableToSignIn.message',
                )}
              </Text>
              <Text>
                {i18n.t(
                  'SignIn.content.apiResponseErrors.unableToSignIn.instructions',
                )}
              </Text>
            </Stack>
          ),
          primaryAction: {
            label: i18n.t(
              'SignIn.content.apiResponseErrors.unableToSignIn.okCap',
            ),
          },
        });

        notifyApp('adc-webview:signin', { status: err.status, data });
      }
    },
    [deviceId, appVersion, osType, osVersion, dispatch],
  );

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues: {
      ...initialValues,
      phone: phone ?? initialValues.phone,
    },
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit: async (formValues: MyFormValues) => {
      await signInRequest({ values: formValues });
    },
    validateOnBlur: true,
  });

  const handleClickForgotPass = useCallback(() => {
    mediator.publish('router:navigate', {
      url: '/forgot-password',
      query: {
        ...query,
      },
    });
  }, [query]);

  const handleCreateAccount = useCallback(() => {
    mediator.publish('router:navigate', {
      url: '/create-account',
      query: {
        ...query,
      },
    });
  }, [query]);

  return (
    <MainContainer testID={testID}>
      {(isSubmitting || isLoading) && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}
      <InformationalScreen
        testID={buildTestId({ testID, suffix: 'screen' })}
        topNavigationProps={{
          animatedTitle: true,
          title: i18n.t('SignIn.title'),
          buttonStart: {
            element: <TopHeaderBackArrow testID="nav-back" />,
            label: 'Go Back',
            onPress: () => {
              notifyApp('adc-webview:navigate-back-app');
            },
          },
          testID: 'nav-back',
        }}
      >
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8}>{i18n.t('SignIn.subtitle')}</Text>
            <FormContainer>
              <PhoneInput
                aria-label={i18n.t(
                  'SignIn.content.signInForm.formField.phone.label',
                )}
                placeholder={i18n.t(
                  'SignIn.content.signInForm.formField.phone.placeholder',
                )}
                label={i18n.t(
                  'SignIn.content.signInForm.formField.phone.label',
                )}
                errorMessage={errors.phone}
                isInvalid={Boolean(errors.phone && touched.phone)}
                value={values.phone}
                testID={buildTestId({ testID, suffix: ['phone', 'input'] })}
                onChangeText={handleChange('phone')}
                onBlur={handleBlur('phone')}
                disabled={!!phone}
              />
              <InputPassword
                placeholder={i18n.t(
                  'SignIn.content.signInForm.formField.password.placeholder',
                )}
                label={i18n.t(
                  'SignIn.content.signInForm.formField.password.label',
                )}
                isInvalid={Boolean(errors.password && touched.password)}
                errorMessage={errors.password}
                testID={buildTestId({ testID, suffix: ['password', 'input'] })}
                onBlur={handleBlur('password')}
                onChangeText={handleChange('password')}
                defaultValue={values.password}
              />
              <Stack flexDirection="column" alignItems="center">
                <Button
                  variant="tertiary"
                  label={i18n.t('SignIn.content.signInForm.secondaryText')}
                  onPress={handleClickForgotPass}
                  testID={buildTestId({
                    testID,
                    suffix: ['forgot', 'password', 'button'],
                  })}
                />
              </Stack>
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'SignIn.content.footerCreateAccount.links.signIn.label',
              )}
              onButtonPress={handleSubmit}
              buttonTestID={buildTestId({
                testID,
                suffix: ['sign', 'in', 'button'],
              })}
              disabled={!isValid || !dirty || isSubmitting}
              onSecondaryButtonPress={handleCreateAccount}
              secondaryButtonTestID={buildTestId({
                testID,
                suffix: ['create', 'account', 'button'],
              })}
              secondarySectionLabel={i18n.t(
                'SignIn.content.footerCreateAccount.primaryText',
              )}
              secondaryButtonLabel={i18n.t(
                'SignIn.content.footerCreateAccount.links.createAccount.label',
              )}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(SignIn);
